import { useCallback, type ReactNode } from 'react';
import { useSessionStore } from '../stores/session';

/**
 * IMPORTANT: this module hides controls for USABILITY ONLY. It is not a
 * security boundary. Every permission is re-checked server-side on every
 * request (CONVENTIONS.md §4) — a user who forges a client bundle or calls
 * the API directly is stopped there, not here. Never reason about this file
 * as if it were access control.
 */

export function usePermission(): (permission: string) => boolean {
  const permissions = useSessionStore((s) => s.permissions);
  return useCallback((permission: string) => permissions.includes(permission), [permissions]);
}

export function hasPermission(permissions: string[], permission: string): boolean {
  return permissions.includes(permission);
}

export interface CanProps {
  permission: string;
  /** Render this instead when the permission is absent. Defaults to nothing. */
  fallback?: ReactNode;
  children: ReactNode;
}

/** Conditionally renders children when the active session holds `permission`. */
export function Can({ permission, fallback = null, children }: CanProps) {
  const can = usePermission();
  return <>{can(permission) ? children : fallback}</>;
}
